/** Integration monitoring (M15-60, M15-62): calls, errors, exceptions and sync status. */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, Inbox, KeyRound, Webhook } from 'lucide-react';
import { api } from '../../lib/api';
import { relative } from '../../lib/format';
import { Alert, Card, EmptyState, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th } from '../../components/ui';

interface Dashboard {
  clients: { id: string; name: string; status: string; lastUsedAt: string | null; calls24h: number; errors24h: number; errorRate: number }[];
  exceptions: number;
  exceptionsByCode: { code: string | null; count: number }[];
  pendingDaily: number;
  lastSync: { externalSystem: string; lastPostedAt: string | null; events: number }[];
  webhooks: { id: string; url: string; active: boolean; failureCount: number; disabledAt: string | null }[];
  volume: { day: string; calls: number; errors: number }[];
  alerts: { level: 'warn' | 'error'; message: string }[];
}

function Kpi({ label, value, Icon, to, tone }: { label: string; value: number | string; Icon: typeof Inbox; to?: string; tone?: 'critical' }): JSX.Element {
  const body = (
    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-brand/40 dark:border-slate-800 dark:bg-slate-900">
      <span className={`rounded-xl p-2 ${tone ? 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-200' : 'bg-brand-50 text-brand-700 dark:bg-slate-800 dark:text-brand-100'}`}>
        <Icon aria-hidden="true" className="h-5 w-5" />
      </span>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
        <p className="text-2xl font-bold tabular-nums text-slate-900 dark:text-white">{value}</p>
      </div>
    </div>
  );
  return to ? <Link to={to} className="block rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand/40">{body}</Link> : body;
}

export function IntegrationDashboardPage(): JSX.Element {
  const q = useQuery({ queryKey: ['integration-dashboard'], queryFn: () => api<Dashboard>('/integrations/dashboard'), refetchInterval: 30_000 });
  const d = q.data;
  const max = Math.max(1, ...(d?.volume.map((v) => v.calls) ?? [1]));
  const calls = d?.clients.reduce((s, c) => s + c.calls24h, 0) ?? 0;
  return (
    <>
      <PageHeader title="Integrations" description="API traffic, the exception queue and webhook health, refreshed every 30 seconds." />
      {q.isLoading && <Skeleton className="h-64 w-full" />}
      {q.error && <Alert tone="critical">{(q.error as Error).message}</Alert>}
      {d && (
        <div className="space-y-4">
          {d.alerts.length > 0 && (
            <div className="space-y-2">
              {d.alerts.map((a, i) => (
                <Alert key={i} tone={a.level === 'error' ? 'critical' : 'warning'}>
                  <span className="inline-flex items-center gap-2"><AlertTriangle aria-hidden="true" className="h-4 w-4 shrink-0" />{a.message}</span>
                </Alert>
              ))}
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi label="API calls (24 h)" value={calls.toLocaleString()} Icon={KeyRound} to="/integrations/clients" />
            <Kpi label="Exceptions" value={d.exceptions} Icon={Inbox} to="/integrations/events?status=EXCEPTION" tone={d.exceptions ? 'critical' : undefined} />
            <Kpi label="Waiting for daily summary" value={d.pendingDaily} Icon={Inbox} to="/integrations/events?status=RECEIVED" />
            <Kpi label="Webhooks active" value={`${d.webhooks.filter((w) => w.active).length} / ${d.webhooks.length}`} Icon={Webhook} to="/integrations/webhooks" />
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <Card title="API clients" description="Last 24 hours" className="lg:col-span-2">
              {d.clients.length === 0 ? (
                <EmptyState title="No API clients yet" description="Register one under API Clients to connect a POS, HIS or payroll system." />
              ) : (
                <TableWrap>
                  <thead><tr><Th>Client</Th><Th>Status</Th><Th align="right">Calls</Th><Th align="right">Errors</Th><Th>Last used</Th></tr></thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {d.clients.map((c) => (
                      <tr key={c.id}>
                        <Td className="font-medium">{c.name}</Td>
                        <Td><StatusChip tone={c.status === 'ACTIVE' ? 'ok' : c.status === 'DISABLED' ? 'critical' : 'warning'}>{c.status.replace('_', ' ').toLowerCase()}</StatusChip></Td>
                        <Td align="right" className="tabular-nums">{c.calls24h}</Td>
                        <Td align="right" className={`tabular-nums ${c.errorRate > 5 ? 'font-semibold text-red-600' : ''}`}>{c.errors24h} ({c.errorRate}%)</Td>
                        <Td className="text-slate-500">{c.lastUsedAt ? relative(c.lastUsedAt) : 'Never'}</Td>
                      </tr>
                    ))}
                  </tbody>
                </TableWrap>
              )}
            </Card>
            <Card title="Exceptions by reason">
              {d.exceptionsByCode.length === 0 ? <p className="text-sm text-slate-500">The queue is empty.</p> : (
                <ul className="space-y-2 text-sm">
                  {d.exceptionsByCode.map((e) => (
                    <li key={e.code ?? '-'} className="flex items-center justify-between">
                      <code className="text-xs">{e.code}</code>
                      <span className="font-semibold tabular-nums">{e.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card title="Calls per day" description="Last 7 days; errors in red">
              {d.volume.length === 0 ? <p className="text-sm text-slate-500">No calls yet.</p> : (
                <div className="flex h-40 items-end gap-2" role="img" aria-label="API calls per day">
                  {d.volume.map((v) => (
                    <div key={v.day} className="flex max-w-[4rem] flex-1 flex-col items-center gap-1">
                      <div className="relative w-full rounded-t-md bg-brand/70" style={{ height: `${(v.calls / max) * 120}px` }} title={`${v.day}: ${v.calls} calls, ${v.errors} errors`}>
                        <div className="absolute bottom-0 w-full rounded-t-md bg-red-500" style={{ height: `${v.calls ? (v.errors / v.calls) * 100 : 0}%` }} />
                      </div>
                      <span className="text-[10px] text-slate-500">{v.day.slice(5)}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
            <Card title="Last successful sync" description="Per external system">
              {d.lastSync.length === 0 ? <p className="text-sm text-slate-500">No events posted yet.</p> : (
                <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
                  {d.lastSync.map((s) => (
                    <li key={s.externalSystem} className="flex items-center justify-between py-2">
                      <span className="font-medium">{s.externalSystem}</span>
                      <span className="text-slate-500">{s.lastPostedAt ? relative(s.lastPostedAt) : '—'} · {s.events} events</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </div>
      )}
    </>
  );
}
